import GreyLoadingOverlay from '@/components/loading/GreyLoadingOverlay';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Button,
  Dialog,
  Drawer,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { EPurchaseSectionStep, PurchaseSectionContext } from '.';
import { moneyFormat } from '../../../common/numberHelpers';
import {
  BANK_ACCOUNT_ID,
  BANK_ACCOUNT_NAME,
  BANK_BRANCH_NAME,
} from '../../../constant/payment';
import { copyToClipboard } from '../../../helpers/copyToClipboard';
import useConfirmBanking from '../../../hooks/course/useConfirmBanking';
import useToast from '../../../hooks/useToast';
import { OutlinedCopyIcon, OutlinedInfoCircleIcon } from '../svgIcon/outlined';
import PurchaseContactSection from './component';

const TransactionInfo = () => {
  const { setOpenStep, openStep } = useContext(PurchaseSectionContext);

  const toggle = () => setOpenStep(undefined);

  const matches = useMediaQuery('(min-width:768px)');
  return matches ? (
    <Dialog
      open={openStep === EPurchaseSectionStep.TransactionInfo}
      onClose={toggle}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 120,
      }}
    >
      <TransactionInfo.Header toggle={toggle} />
      <TransactionInfo.InfoSection />
    </Dialog>
  ) : (
    <Drawer
      anchor={'bottom'}
      open={openStep === EPurchaseSectionStep.TransactionInfo}
      onClose={toggle}
      PaperProps={{
        className: '!bg-[#FCFCFC] !rounded-[10px]',
      }}
      sx={{
        zIndex: 120,
      }}
    >
      <TransactionInfo.Header toggle={toggle} />
      <TransactionInfo.InfoSection />
    </Drawer>
  );
};

TransactionInfo.Header = function Header({ toggle }: { toggle: () => void }) {
  return (
    <div className="flex items-center justify-between p-4 text-[#000000DE] md:p-6">
      <h1 className="text-2xl font-bold leading-[140%]">
        Thông tin chuyển khoản
      </h1>
      <IconButton onClick={toggle}>
        <CloseRoundedIcon className="text-[#000000DE]" />
      </IconButton>
    </div>
  );
};

const CanCopyText = ({ content }: { content: string }) => {
  const toast = useToast();
  const handleCopy = () =>
    copyToClipboard(content, () => toast('Sao chép thành công', 'success'));

  return (
    <div className="flex items-center justify-between gap-2">
      <p className="text-right text-lg font-medium leading-[100%] text-Text/Lightbackground/Primary">
        {content}
      </p>
      <IconButton onClick={handleCopy}>
        <OutlinedCopyIcon className="text-Action/Active" />
      </IconButton>
    </div>
  );
};

TransactionInfo.InfoSection = function InfoSection() {
  const { price, order } = useContext(PurchaseSectionContext);

  return (
    <div className="flex flex-col gap-4 px-6 pb-6 pt-8">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-Text/Lightbackground/Secondary">
          Mã đơn hàng:
        </p>
        <CanCopyText content={order.orderCode} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-Text/Lightbackground/Secondary">
          Thông tin người nhận:
        </p>
        <CanCopyText content={BANK_ACCOUNT_NAME} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-Text/Lightbackground/Secondary">
          Số tài khoản:
        </p>
        <CanCopyText content={BANK_ACCOUNT_ID} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-Text/Lightbackground/Secondary">
          Ngân hàng:
        </p>
        <CanCopyText content={BANK_BRANCH_NAME} />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-Text/Lightbackground/Secondary">
            Nội dung CK:
          </p>
          <p className="flex flex-col items-end gap-1 text-lg font-medium leading-[100%] md:flex-row md:items-center">
            <span>{'<Số điện thoại của bạn>'}</span>
            <span>{'<Mã đơn hàng>'}</span>
          </p>
        </div>
        <p className="block w-full text-right text-sm font-medium text-Text/Lightbackground/Secondary">
          VD: 0949138929_123456DOCCEN12XX3
        </p>
      </div>

      <hr className="h-[1px] bg-[#E0E0E0]" />

      <div className="flex items-center justify-between text-sm font-medium leading-[120%]">
        <p>Tổng thanh toán:</p>
        <p className="w-max text-2xl font-semibold leading-[140%] text-Success/Main">
          {moneyFormat(price || 0)} VNĐ
        </p>
      </div>

      <div className="flex items-center gap-2 text-sm font-medium leading-[120%] text-Text/Lightbackground/Secondary">
        <OutlinedInfoCircleIcon
          className="flex-shrink-0"
          height={16}
          width={16}
        />
        <ul className="my-0">
          <li>
            Bằng việc xác nhận thanh toán, hệ thống sẽ ghi nhận thông tin thanh
            toán đơn hàng và tiến hành xác nhận với ngân hàng.
          </li>
          <li>
            Thông báo mua khóa học thành công sẽ được gửi đến email đăng ký của
            bạn sau khi giao dịch được ngân hàng xác nhận.
          </li>
        </ul>
      </div>

      <hr className="h-[1px] bg-[#E0E0E0]" />

      <div className="flex flex-col-reverse gap-4 md:flex-col">
        <PurchaseContactSection />
        <TransactionInfo.ConfirmButton />
      </div>
    </div>
  );
};

TransactionInfo.ConfirmButton = function ConfirmButton() {
  const toast = useToast();

  const router = useRouter();
  const { cid } = router.query;

  const {
    mutationReturn: { mutate, isLoading },
  } = useConfirmBanking({
    opt: {
      onSuccess() {
        setOpenStep(undefined);
      },
      onError() {
        toast('Có lỗi xảy ra, vui lòng thử lại sau.', 'error');
      },
    },
  });

  const { order, setOpenStep } = useContext(PurchaseSectionContext);

  const matches = useMediaQuery('(min-width:768px)');

  return (
    <div className="relative md:ml-auto md:w-fit">
      <GreyLoadingOverlay show={isLoading} size={30} />
      <Button
        onClick={() =>
          mutate({
            orderId: order.orderId,
            courseId: cid as string,
          })
        }
        fullWidth={!matches}
        variant="high_emphasis_v1"
        className="!py-2"
      >
        Xác nhận đã thanh toán
      </Button>
    </div>
  );
};

export default TransactionInfo;
